import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useClients } from '@/hooks/useClients';
import { useRooms } from '@/hooks/useRooms';
import { useBedCountEntries, type BedCount, type BedCountResult } from '@/hooks/useBedCount';
import { AvcReportCard } from './AvcReportCard';

interface BedCountPrintViewProps {
  bedCount: BedCount;
  siteName?: string;
}

const STATUS_LABEL: Record<BedCountResult, string> = {
  present: 'Present',
  not_present: 'Not Present',
  unbedded: 'Unbedded',
  pass_acknowledged: 'On Pass — Acknowledged',
};

/**
 * Printable bed count sheet — reflects the marked status from the on-screen sheet.
 * Includes the auto-generated AVC report block at the top.
 */
export const BedCountPrintView = ({ bedCount, siteName }: BedCountPrintViewProps) => {
  const { clients } = useClients();
  const { rooms } = useRooms();
  const { data: entries = [] } = useBedCountEntries(bedCount.id);

  const completedByIds = useMemo(
    () => Array.from(new Set(entries.map((e) => e.marked_by).filter(Boolean) as string[])),
    [entries]
  );

  // Resolve names
  const userIds = Array.from(new Set([bedCount.started_by, bedCount.signed_off_by, bedCount.called_in_by, ...completedByIds]
    .filter(Boolean) as string[]));
  const { data: userNames = {} } = useQuery({
    queryKey: ['bed-count-user-names', userIds.sort().join(',')],
    enabled: userIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', userIds);
      if (error) throw error;
      const map: Record<string, string> = {};
      (data ?? []).forEach((p: any) => { map[p.id] = p.full_name || 'Unknown'; });
      return map;
    },
  });
  const startedByName = bedCount.started_by ? (userNames[bedCount.started_by] || '—') : null;
  const signedOffByName = bedCount.signed_off_by ? (userNames[bedCount.signed_off_by] || '—') : null;
  const calledInByName = bedCount.called_in_by ? (userNames[bedCount.called_in_by] || '—') : null;
  const completedByNames = completedByIds.map((id) => userNames[id] || '—').join(', ');

  const clientById = useMemo(() => {
    const m = new Map<string, any>();
    clients.forEach((c: any) => m.set(c.id, c));
    return m;
  }, [clients]);

  const roomById = useMemo(() => {
    const m = new Map<string, any>();
    rooms.forEach((r: any) => m.set(r.id, r));
    return m;
  }, [rooms]);

  const sorted = useMemo(() => {
    return [...entries].sort((a, b) => {
      const ra = roomById.get(a.room_id ?? '')?.room_number ?? '';
      const rb = roomById.get(b.room_id ?? '')?.room_number ?? '';
      return String(ra).localeCompare(String(rb), undefined, { numeric: true });
    });
  }, [entries, roomById]);

  const scheduled = new Date(bedCount.scheduled_for);
  const startedAt = bedCount.started_at ? new Date(bedCount.started_at) : null;
  const signedOffAt = bedCount.signed_off_at ? new Date(bedCount.signed_off_at) : null;
  const calledInAt = bedCount.called_in_at ? new Date(bedCount.called_in_at) : null;

  const fmtTime = (d: Date) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const renderMark = (entry: typeof sorted[number], target: BedCountResult) =>
    entry.result === target ? '☑' : '☐';

  return (
    <div className="bed-count-print p-6 bg-background text-foreground">
      <div className="print-header text-center pb-4 mb-4 border-b">
        <h1 className="text-2xl font-bold">Bed Count Sheet</h1>
        <div className="text-sm mt-1">
          {siteName ?? 'Facility'} · {scheduled.toLocaleDateString()} · Scheduled {fmtTime(scheduled)}
        </div>
      </div>

      {/* AVC Report block (printable mode) */}
      <div className="mb-6">
        <AvcReportCard bedCount={bedCount} siteId={bedCount.site_id} printMode />
      </div>

      <h2 className="text-lg font-semibold mb-2">Roster</h2>
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b">
            <th className="text-left p-2 w-16">Bed</th>
            <th className="text-left p-2">Client</th>
            <th className="text-left p-2 w-20">Expected</th>
            <th className="text-center p-2 w-16">Present</th>
            <th className="text-center p-2 w-20">Not Present</th>
            <th className="text-center p-2 w-16">Unbed</th>
            <th className="text-center p-2 w-20">On Pass</th>
            <th className="text-left p-2 w-40">Result / Notes</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((e) => {
            const c = clientById.get(e.client_id);
            const r = roomById.get(e.room_id ?? '');
            const fullName = c
              ? (`${c.first_name ?? ''} ${c.last_name ?? ''}`.trim() || c.name?.trim() || 'Unknown')
              : 'Unknown';
            const isPass = e.expected_status === 'on_pass';
            const resultLabel = e.result ? STATUS_LABEL[e.result] : '—';
            return (
              <tr key={e.id} className="border-b">
                <td className="p-2 font-medium">{r?.room_number ?? '—'}</td>
                <td className="p-2">{fullName}</td>
                <td className="p-2 text-xs">{isPass ? 'On Pass' : 'Bedded'}</td>
                <td className="p-2 text-center">{isPass ? '—' : renderMark(e, 'present')}</td>
                <td className="p-2 text-center">{isPass ? '—' : renderMark(e, 'not_present')}</td>
                <td className="p-2 text-center">{isPass ? '—' : renderMark(e, 'unbedded')}</td>
                <td className="p-2 text-center">{isPass ? renderMark(e, 'pass_acknowledged') : '—'}</td>
                <td className="p-2 border-l border-dashed text-xs">
                  <div className="font-medium">{resultLabel}</div>
                  {e.notes && <div className="text-muted-foreground">{e.notes}</div>}
                </td>
              </tr>
            );
          })}
          {sorted.length === 0 && (
            <tr>
              <td colSpan={8} className="p-4 text-center text-muted-foreground">
                No bedded clients at this facility.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="mt-12 pt-6 border-t text-sm">
        <div className="grid grid-cols-4 gap-6">
          <div>
            <div className="border-b pb-1 mb-1">
              {startedByName && <span className="font-medium">{startedByName}</span>}&nbsp;
            </div>
            <div className="text-xs">Started by {startedAt && <>· {fmtTime(startedAt)}</>}</div>
          </div>
          <div>
            <div className="border-b pb-1 mb-1">
              {completedByNames && <span className="font-medium">{completedByNames}</span>}&nbsp;
            </div>
            <div className="text-xs">Completed by</div>
          </div>
          <div>
            <div className="border-b pb-1 mb-1">
              {signedOffByName && <span className="font-medium">{signedOffByName}</span>}&nbsp;
            </div>
            <div className="text-xs">Signed off {signedOffAt && <>· {fmtTime(signedOffAt)}</>}</div>
          </div>
          <div>
            <div className="border-b pb-1 mb-1">
              {calledInByName && <span className="font-medium">{calledInByName}</span>}&nbsp;
            </div>
            <div className="text-xs">Called in to AVC {calledInAt && <>· {fmtTime(calledInAt)}</>}</div>
          </div>
        </div>
      </div>
    </div>
  );
};
